import '@spreeai/web-sdk/dist/web-sdk.css';
import { useQuery } from '@tanstack/react-query';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import garmentService, { GarmentV3DTO } from '../../Services/garmentService';
import sizingService from '../../Services/sizingService';
import ColorSwatch from '../ColorSwatch';
import RadioButton from '../RadioButton';
import { TryOnButton } from '../TryOnButton/TryOnButton';
import { NextButton, PrevButton, usePrevNextButtons } from '../ui/CarouselButtons/CarouselButtons';
import { Skeleton } from '../ui/Skeleton';
import { Typography } from '../ui/Typography/Typography';
import styles from './index.module.scss';

interface ProductCardProps {
    garment: GarmentV3DTO;
}

const useGetOutfits = (garmentId: string) => {
    return useQuery({
        queryKey: ['outfits', garmentId],
        queryFn: async () => {
            const response = await garmentService.fetchOutfitsByGarmentIds([garmentId]);
            return response;
        },
        enabled: !!garmentId,
    });
};

const useGetSizeChartMapping = ({
    partnerId,
    brand,
    garmentCategory,
}: {
    partnerId: string;
    brand: string;
    garmentCategory: string;
}) => {
    return useQuery({
        queryKey: ['sizeChartMapping', partnerId, brand, garmentCategory],
        queryFn: async () => {
            const response = await sizingService.getSizeChartMapping({
                partnerId,
                brand,
                garment_category: garmentCategory,
            });
            return response;
        },
    });
};

const useGetSizeCharts = (garmentId: string) => {
    return useQuery({
        queryKey: ['sizeCharts', garmentId],
        queryFn: async () => {
            const response = await sizingService.getSizeCharts({ garmentId });
            return response;
        },
        enabled: !!garmentId,
    });
};

const ProductCard: React.FC<ProductCardProps> = ({ garment }) => {
    const [selectedColor, setSelectedColor] = useState(garment.variants[0].color);
    // Get all available sizes from the first variant's size_groups
    const availableSizes = garment.variants[0].size_groups?.flatMap((group) => group.sizes) ?? [];
    const [selectedSize, setSelectedSize] = useState(availableSizes[0] || '');
    const { data: outfits } = useGetOutfits(garment.id);

    const availableOutfit = outfits?.outfits.find(
        (outfit) => outfit.status === 'AVAILABLE' && outfit.garments.every((g) => g.available)
    );

    const filteredVariants = garment.variants.filter((variant) => variant.color === selectedColor);
    const uniqueSizes = Array.from(
        new Set(
            filteredVariants.flatMap(
                (variant) => variant.size_groups?.flatMap((group) => group.sizes) ?? []
            )
        )
    );
    // const selectedVariant = filteredVariants.find((variant) =>
    //     variant.size_groups.some((group) => group.sizes.includes(selectedSize))
    // );
    // const [price, setPrice] = useState(selectedVariant ? selectedVariant.price : 0);

    const handleColorChange = (colorName: string) => {
        const newColor = garment.variants.find(
            (variant) => variant.color.name === colorName
        )?.color;
        if (newColor) {
            setSelectedColor(newColor);
            // const newVariant = garment.variants.find(
            //     (variant) => variant.color.name === colorName &&
            //     variant.size_groups.some((group) => group.sizes.includes(selectedSize))
            // );
            // setPrice(newVariant ? newVariant.price : 0);
        }
    };

    const handleSizeChange = (size: string) => {
        setSelectedSize(size);
        // const newVariant = filteredVariants.find((variant) =>
        //     variant.size_groups.some((group) => group.sizes.includes(size))
        // );
        // setPrice(newVariant ? newVariant.price : 0);
    };

    return (
        <div className={styles.productContainer}>
            <Carousel
                images={garment.variants[0]?.images || []}
                garment={garment}
                availableOutfit={Boolean(availableOutfit)}
            />
            <div className={styles.productDetails}>
                <div>
                    <div className={styles.productDesigner}>
                        {/* <Typography>{garment.designer.name}</Typography> /{' '} */}
                        <Typography>
                            {garment.tags.find((tag) => {
                                return tag === 'women' || tag === 'men';
                            }) + 's'}
                        </Typography>
                    </div>
                    <Typography variant="heading3" className={styles.productTitle}>
                        {garment.title.toLowerCase()}
                    </Typography>
                </div>
                {uniqueSizes.length > 0 ? (
                    <div className={styles.productOptions}>
                        <div className={styles.sizingHeader}>
                            <Typography>Size:</Typography>{' '}
                            {/* {CONSTANTS.IS_DEV || CONSTANTS.IS_STAGE ? (
                                <SizingButton
                                    garments={[{ garmentId: garment.id }]}
                                    partnerId={garment.partner_id}
                                />
                            ) : null} */}
                        </div>

                        <div className={styles.product__sizeButtonsContainer}>
                            {uniqueSizes.map((size) => (
                                <RadioButton
                                    key={size}
                                    id={size}
                                    name="size"
                                    value={size}
                                    checked={selectedSize === size}
                                    onChange={handleSizeChange}
                                    label={size}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
                <div className={styles.productOptions}>
                    <Typography>Color:</Typography>
                    <div className={styles.product__sizeButtonsContainer}>
                        {Array.from(
                            new Map(
                                garment.variants.map((variant) => [
                                    variant.color.name,
                                    variant.color,
                                ])
                            ).values()
                        ).map((color) => (
                            <ColorSwatch
                                key={color.name}
                                name={color.name}
                                hex={color.hex}
                                checked={selectedColor.name === color.name}
                                onChange={handleColorChange}
                            />
                        ))}
                    </div>
                </div>
                <div className={styles.productProductDetails}>
                    <Typography color="secondary">{garment.description}</Typography>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;

export const ProductDetailSkeleton = () => {
    const { t } = useTranslation();

    return (
        <div className={styles.productContainer}>
            <Skeleton width="100%" height="400px" style={{ aspectRatio: '2/3' }} />
            <div className={styles.productDetails}>
                <div className={styles.skeletonHeader}>
                    <div className={styles.productDesigner}>
                        <Skeleton width="42px" height="16px" />
                        <Skeleton width="42px" height="16px" />
                    </div>
                    <Skeleton width="75%" height="32px" />
                </div>
                <div className={styles.productOptions}>
                    <Typography>{t('productDetail.color')}:</Typography>{' '}
                    <div className={styles.product__sizeButtonsContainer}>
                        <Skeleton width="50px" height="50px" />
                        <Skeleton width="50px" height="50px" />
                        <Skeleton width="50px" height="50px" />
                    </div>
                </div>
                <div className={styles.productOptions}>
                    <Typography>{t('productDetail.size')}:</Typography>
                    <div className={styles.product__sizeButtonsContainer}>
                        <Skeleton width="50px" height="50px" />
                        <Skeleton width="50px" height="50px" />
                        <Skeleton width="50px" height="50px" />
                    </div>
                </div>
                <div className={styles.productProductDetails}>
                    <div className={styles.skeletonDescription}>
                        <Skeleton width="100%" height="16px" />
                        <Skeleton width="100%" height="16px" />
                        <Skeleton width="100%" height="16px" />
                    </div>
                </div>
            </div>
        </div>
    );
};

interface CarouselProps {
    images: Array<{ url: string }>;
    garment: GarmentV3DTO;
    availableOutfit: boolean;
}

const Carousel = ({ images, garment, availableOutfit }: CarouselProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
    const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
    const [scrollProgress, setScrollProgress] = useState(0);

    const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
        const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
        setScrollProgress(progress * 100);
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onScroll(emblaApi);
        emblaApi.on('reInit', onScroll).on('scroll', onScroll).on('slideFocus', onScroll);
    }, [emblaApi, onScroll]);

    return (
        <div className={styles.carouselContainer}>
            <div className={styles.outfitBadge}>
                <TryOnButton
                    garments={[{ garmentId: garment.id }]}
                    text="Try This Item"
                    variant="icon"
                    partnerId={garment.partner_id}
                />
            </div>
            <section className={styles.embla}>
                <div className={styles.embla__viewport} ref={emblaRef}>
                    <div className={styles.embla__container}>
                        {images.map((image, index) => (
                            <div className={styles.embla__slide} key={index}>
                                <img
                                    className={styles.embla__slide__img}
                                    src={image.url}
                                    alt={`Slide ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <PrevButton className={styles.prevButton} onClick={onPrevButtonClick} />
                <NextButton className={styles.nextButton} onClick={onNextButtonClick} />
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ transform: `translate3d(${scrollProgress}%,0px,0px)` }}
                    />
                </div>
            </section>
        </div>
    );
};
